import { cn } from "@/lib/cn";

/** One cell of the strip. */
export interface MetricCell {
  /** Stable key — also the test handle. */
  id: string;
  /** Small uppercase label — «МАРШРУТЫ». */
  label: string;
  /**
   * The headline figure, already formatted, or **null for NOT MEASURED**.
   *
   * `null` renders a muted «—»; a measured zero must arrive as the string
   * `"0"`. The distinction is load-bearing (DEV_RULES / `stops_undelivered`):
   * a failed request must never render as a confident zero.
   */
  value: string | null;
  /** Unit or qualifier printed small beside the figure — «из 16 план». */
  valueSuffix?: string | null;
  /** Secondary context line, or null when there is nothing to add. */
  sub?: string | null;
  /** Optional 0–100 progress bar under the figure (the mockup's «Точки» cell). */
  progressPercent?: number | null;
  /** Renders the figure in the danger token — used for a non-zero failure count. */
  emphasis?: "danger" | null;
}

interface Props {
  cells: MetricCell[];
}

const EM_DASH = "—";

/**
 * The quiet six-figure strip under the action band.
 *
 * Deliberately NOT built on `KpiCard`: that is a tall gradient chip whose
 * accent maps hold literal palette classes and raw hex, so it would both fight
 * this layout and drag hardcoded colour in (DEV_RULES §10.10).
 *
 * Responsive: two columns on a phone, three on a small tablet, all six in one
 * row from `lg` up — the 1280×800 design target.
 */
export function MetricStrip({ cells }: Props) {
  return (
    <div className="grid grid-cols-2 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm sm:grid-cols-3 lg:grid-cols-6">
      {cells.map((cell) => (
        <div
          key={cell.id}
          className="flex flex-col gap-1.5 border-b border-r border-gray-100 p-4 last:border-r-0 sm:[&:nth-child(3n)]:border-r-0 lg:border-b-0 lg:[&:nth-child(3n)]:border-r lg:[&:nth-child(6n)]:border-r-0"
        >
          <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">
            {cell.label}
          </p>

          <p className="flex items-baseline gap-1.5">
            <span
              className={cn(
                "text-2xl font-bold leading-none tracking-tight",
                cell.value == null
                  ? "text-gray-300"
                  : cell.emphasis === "danger"
                    ? "text-brand-danger"
                    : "text-gray-900",
              )}
            >
              {cell.value ?? EM_DASH}
            </span>
            {cell.value != null && cell.valueSuffix ? (
              <span className="text-xs font-medium text-gray-500">
                {cell.valueSuffix}
              </span>
            ) : null}
          </p>

          {cell.progressPercent != null && (
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
              {/* A computed percentage is DATA, not styling, and cannot be a
                  Tailwind class (it is continuous). `style={{ width }}` with a
                  `data-pct` handle is this project's established pattern for
                  exactly that — see `RouteFactsMetricStrip`. The COLOUR stays a
                  token (`bg-brand-primary`); only the length is runtime. */}
              <div
                data-testid={`metric-progress-${cell.id}`}
                data-pct={String(
                  Math.round(clampPercent(cell.progressPercent)),
                )}
                className="h-full rounded-full bg-brand-primary transition-all"
                style={{ width: `${clampPercent(cell.progressPercent)}%` }}
              />
            </div>
          )}

          {cell.sub ? (
            <p className="text-xs leading-4 text-gray-400">{cell.sub}</p>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function clampPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, value));
}
